// Rendering a DATE column without moving it a day.
//
// THE TRAP
//
// `users.member_since` and `memberships.start_date` are Postgres `DATE`. The
// server patches node-pg's `timestamp` parser (OID 1114) to UTC but not `DATE`
// (OID 1082), so a DATE comes back built at the Node process's local midnight.
// Production runs UTC - confirmed on the container, offset 0 - so the API
// serialises 2024-10-11 as "2024-10-11T00:00:00.000Z".
//
// `new Date(...).toLocaleDateString()` then renders that in the BROWSER's zone.
// Anywhere west of UTC, which is the entire club, midnight UTC is the previous
// evening, so "Member since Oct 11" renders as Oct 10. `formatMonth` is worse:
// a start_date of 1 January renders as "Dec", off by a whole month on the
// member's own card.
//
// THE FIX, AND WHY IT LIVES HERE
//
// Formatting in UTC restores the date the database holds. That is a one-line
// change, and it had already been made in two places and missed in five -
// `Users.js` even carried a local `new Date(value + 'T12:00:00') // Use noon to
// avoid timezone issues`, which is somebody hitting this and patching their own
// call site. Scattered fixes are why it was still broken.
//
// This is the symptom-level fix done once. The cause is the missing 1082 type
// parser on the server; correcting that changes the shape every DATE consumer
// receives, so it is a deliberate sweep rather than something to slip in.

const DAY_OPTIONS = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
const MONTH_OPTIONS = { month: 'short', year: 'numeric', timeZone: 'UTC' };

/**
 * A date-only value as the database holds it. "Oct 11, 2024".
 */
export function formatDateOnly(value, fallback = null) {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-US', DAY_OPTIONS);
}

/**
 * The month a date-only value falls in. "Oct 2024".
 */
export function formatMonthOnly(value, fallback = null) {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-US', MONTH_OPTIONS);
}

/**
 * The day of the month a date-only value falls on, 1-31.
 *
 * Reading it with `getDate()` is the same trap: it answers in the browser's
 * zone, so the 1st becomes the 31st of the month before. Used where a renewal
 * day is being derived from a picked date.
 */
export function dayOfMonthUTC(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.getUTCDate();
}

export default formatDateOnly;
